import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, renderSlot as _renderSlot, normalizeClass as _normalizeClass, unref as _unref, resolveComponent as _resolveComponent, withCtx as _withCtx, Transition as _Transition, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["value"]
const _hoisted_2 = ["value"]
const _hoisted_3 = ["value"]
const _hoisted_4 = ["value"]
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "application-ad" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = {
  key: 0,
  class: "error-message"
}

import { computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import useFormatter from "@/use/formatter";
import useApplication from "@/use/application";
import { useRoute, useRouter } from "vue-router";
import { $api } from "@/services/api";
import Loader from "@/components/shared/Loader.vue";
import StudentChoiceFooter from "@/components/shared/layout/FooterRefi.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import SupportModal from "@/components/modals/SupportModal.vue";
import type { AppData } from "@/models/application";
import StudentChoiceHeader from "@/components/shared/layout/Header.vue";
import { ProductIds } from "@/config/constants";
import useAuthentication from "@/use/authentication";
import { getCookie } from "@/composables/useCookies";
import useApplicationData from "@/composables/application/useApplicationData";


export default /*@__PURE__*/_defineComponent({
  __name: 'ApplicationPage',
  props: {
  externalLinks: {
    type: Boolean,
    default: false,
  },
},
  setup(__props, { expose: __expose }) {

const props = __props;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { flowType } = useApplicationData();

const { formatHexToRgb } = useFormatter();
const { getLenderConfigurationAsync } = useApplication();
const { handleLoginRedirect, handleSilentSignOut } = useAuthentication();

const showSupportModal = ref<boolean>(false);
const error = ref<boolean>(false);
const loading = ref<boolean>(true);

const lid = computed<number>(() => store.state?.lenderData?.lid || route.query?.lid || 0);

const productTypeId = computed<number>(
  () => store.state.appData?.application?.productTypeId || route.query?.productTypeId || 0,
);

const programTypeId = computed<number>(
  () => store.state.appData?.application?.programTypeId || route.query?.programTypeId || 0,
);

const currentState = computed(() => store.state.appData?.currentState);

const loanConfiguration = computed(() => {
  if (
    currentState.value &&
    currentState.value?.name === "Loan Configuration" &&
    productTypeId.value === ProductIds.PEL &&
    loanConfigurationSubStep.value === 3
  ) {
    return true;
  }

  return false;
});

const loanConfigurationSubStep = computed<number>(() => store.state.loanConfigurationModule.currentSubStep);

const leadSourceId = computed(() => localStorage.getItem("leadSourceId") || route.query?.leadSourceId || 1);
const applicationTypeId = computed(() => {
  if (store?.state?.appData?.application?.metadata?.withCoApplicants !== undefined) {
    return store?.state?.appData?.application?.metadata?.withCoApplicants === true ? 2 : 1;
  } else {
    return 0;
  }
});
const lenderData = computed(() => store.state.lenderData);
const themeStyle = computed(() => {
  const defaultPrimaryColor = "#f7941e";
  const defaultSecondaryColor = "#ffffff";
  // SA color: #f7941e
  // Default Green: #01675a
  const themeRgb = formatHexToRgb(lenderData?.value?.primaryColor || defaultPrimaryColor);
  const secondaryRgb = formatHexToRgb(lenderData?.value?.secondaryColor || defaultSecondaryColor);
  const primaryColor = lenderData?.value?.primaryColor || defaultPrimaryColor;
  const secondaryColor = lenderData?.value?.secondaryColor || defaultSecondaryColor;

  return {
    "--theme-color": primaryColor,
    "--theme-color-rgb": `${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}`,
    "--secondary-color": secondaryColor,
    "--secondary-color-rgb": `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`,
  };
});

async function getLenderDataByQuery() {
  const query = route.query as any;

  const lenderConfig = await $api.configuration.getLenderConfiguration(
    query?.lid ?? 0,
    query.programTypeId ?? 0,
    query.productTypeId ?? 0,
  );

  await store.dispatch("updateLenderData", lenderConfig);
}

async function handleAppData(appData: any, query: any) {
  if (appData && !appData?.errors) {
    if (appData?.redirectUrl) {
      window.location.href = `${appData?.redirectUrl}`;
    } else {
      // handle storing lead in localStorage until ready to submit to DB
      window.localStorage.setItem("appInProgress", JSON.stringify(appData));
      await store.dispatch("setAppData", appData);
      // Clear out the query parameters
      query.appId = undefined;

      console.log("vuex state this.appData", store.state?.appData);

      const getLenderConfigurationRequest = {
        appId: store.state?.appData?.application?.id,
        lenderId: undefined,
        productTypeId: undefined,
        programTypeId: undefined,
        flowType: flowType.value,
      };

      const lenderConfig = await getLenderConfigurationAsync(getLenderConfigurationRequest);

      await store.dispatch("updateLenderData", lenderConfig);

      delete query?.lid;
      delete query?.continue;
      delete query?.leadSourceId;

      router.replace({ query });

      const applyLoginCookie = getCookie("apply_login");

      if (applyLoginCookie) {
        handleLoginRedirect();
      }
    }
  }
}

async function startNewApplication(): Promise<any> {
  // only performs check on lower environments
  let query = route.query as any;
  console.log("Inside startNewApplication()");

  query = { ...query, flowTypeId: Number(query.flowTypeId) || null, code: query.code || null };

  const appData: AppData = await $api.leads.processLead(query);

  if (appData && appData.flowType) {
    localStorage.setItem("flow", appData.flowType.toString());
  }

  if (appData?.redirectUrl) {
    await handleSilentSignOut();
    window.location.href = `${appData?.redirectUrl}`;
  }

  const leadSourceId = query.leadSourceId || appData?.application?.leadSourceId || 0;
  const productTypeId = query.productTypeId || appData?.application?.productTypeId || 0;
  const programTypeId = query.programTypeId || appData?.application?.programTypeId || 0;
  const flowTypeId = query.flowType || appData?.flowType || null;

  localStorage.setItem("leadSourceId", leadSourceId);
  localStorage.setItem("product", productTypeId);
  localStorage.setItem("program", programTypeId);
  localStorage.setItem("flow", flowTypeId);

  // Sign out users and remove them when starting a new app
  await handleSilentSignOut();

  // Assumed if we get to this point they are starting a new application,
  // so we process any data passed in as a lead
  // Validate that query parameters exist in query
  await getLenderDataByQuery();

  return appData;
}

onMounted(async () => {
  loading.value = true;
  // TODO: Figure out how we are going to get lender information
  const query = route.query as any;
  const appInProgress = window.localStorage.getItem("appInProgress");
  let appData: AppData | undefined;
  console.log("Application Page mounted", { query });
  if (query.appId) {
    console.log("Retrieving application", query.appId);
    // See if the user is trying to pull up existing application
    appData = await $api.applications.getApplication(query.appId, flowType.value);
    console.log("retrievedApp", appData);
  } else if (appInProgress) {
    console.log("Inside localStorage");
    // User may have an application still saved in local storage we can access
    appData = JSON.parse(appInProgress);

    if (appData?.application?.id) {
      appData = await $api.applications.getApplication(appData.application.id, flowType.value);
    }

    console.log("lenderid matches query?.lid", query?.lid !== appData?.application?.lenderId);
    // compare query?.lid to appData.application.lenderId (for invite flow)
    // start new application when incoming query?.lid does not match appInProgress lenderId at any point in application
    if (query?.lid !== undefined && query?.lid !== appData?.application?.lenderId && query.continue !== "true") {
      console.log("clearing localstorage");
      window.localStorage.clear();
      appData = await startNewApplication();
    }
  } else {
    if (query.continue !== "true") {
      appData = await startNewApplication();
    }
  }

  await handleAppData(appData, query);
  loading.value = false;
});

__expose({
  props,
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_layers = _resolveComponent("font-awesome-layers")!

  return (loading.value)
    ? (_openBlock(), _createBlock(Loader, {
        key: 0,
        stretch: true,
        inverse: true
      }))
    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
        _createVNode(StudentChoiceHeader, {
          style: _normalizeStyle(themeStyle.value)
        }, null, 8, ["style"]),
        _createElementVNode("div", {
          class: _normalizeClass(["application-page", { w100: loanConfiguration.value }]),
          style: _normalizeStyle(themeStyle.value)
        }, [
          _createElementVNode("input", {
            type: "hidden",
            id: "lid",
            name: "lid",
            value: lid.value
          }, null, 8, _hoisted_1),
          _createElementVNode("input", {
            type: "hidden",
            id: "productTypeId",
            name: "productTypeId",
            value: productTypeId.value
          }, null, 8, _hoisted_2),
          _createElementVNode("input", {
            type: "hidden",
            id: "programTypeId",
            name: "programTypeId",
            value: programTypeId.value
          }, null, 8, _hoisted_3),
          _createElementVNode("input", {
            type: "hidden",
            id: "leadSourceId",
            name: "leadSourceId",
            value: leadSourceId.value
          }, null, 8, _hoisted_4),
          _createElementVNode("input", {
            type: "hidden",
            id: "applicationTypeId",
            name: "applicationTypeId",
            value: applicationTypeId.value
          }, null, 8, _hoisted_5),
          _createElementVNode("div", _hoisted_6, [
            (error.value)
              ? (_openBlock(), _createElementBlock("h1", _hoisted_7, "Something has gone wrong."))
              : _renderSlot(_ctx.$slots, "ad", { key: 1 })
          ]),
          (error.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [...(_cache[2] || (_cache[2] = [
                _createElementVNode("p", null, " We apologize, but we are not able to process your request at this time. If you continue to receive this message please contact support. ", -1)
              ]))]))
            : _renderSlot(_ctx.$slots, "default", { key: 1 })
        ], 6),
        (lid.value > 0)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "support-wrapper",
              style: _normalizeStyle(themeStyle.value)
            }, [
              _createElementVNode("a", {
                title: "Support",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showSupportModal.value = true)),
                href: "#",
                class: "support"
              }, [
                _createVNode(_component_font_awesome_layers, null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "fa-solid fa-circle",
                      class: "circle"
                    }),
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "fa-solid fa-headset",
                      transform: "shrink-6",
                      class: "icon",
                      style: { color: 'white' }
                    })
                  ]),
                  _: 1
                })
              ])
            ], 4))
          : _createCommentVNode("", true),
        _createVNode(_Transition, { name: "modal" }, {
          default: _withCtx(() => [
            (showSupportModal.value)
              ? (_openBlock(), _createBlock(SupportModal, {
                  key: 0,
                  style: _normalizeStyle(themeStyle.value),
                  id: "supportModal",
                  onClose: _cache[1] || (_cache[1] = ($event: any) => (showSupportModal.value = false))
                }, null, 8, ["style"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createElementVNode("div", {
          class: "application-page-footer",
          style: _normalizeStyle(themeStyle.value)
        }, [
          _createVNode(StudentChoiceFooter, {
            "external-links": __props.externalLinks,
            "lender-id": lenderData.value?.lid
          }, null, 8, ["external-links", "lender-id"])
        ], 4)
      ], 64))
}
}

})